const KEY = "home_app_data_v1";

// ============================================================================
// PRODUCTION GUARDS: Prevent Business Data in localStorage
// ============================================================================

/**
 * CRITICAL: These keys are FORBIDDEN in localStorage in production
 * 
 * localStorage is NOT a database. It is:
 * - Lost on browser clear
 * - Not synced across devices
 * - Not accessible to backend
 * - Not suitable for business data
 * 
 * Database is single source of truth. Use API.
 */
const FORBIDDEN_BUSINESS_KEYS = [
  'seller',
  'buyer',
  'offer',
  'valuation',
  'payment',
  'document',
  'aml',
  'kyc',
  'onboarding',
  'listing',
  'viewing',
  'search',
  'transaction',
  'contract',
  'lead'
];

/**
 * Guard against storing business data in localStorage
 * 
 * @param {string} key - localStorage key or section name
 * @throws {Error} if key contains forbidden business data pattern
 */
function guardAgainstBusinessData(key) {
  if (!import.meta.env.PROD) {
    // Development: Warning only
    const lowerKey = key.toLowerCase();
    const forbidden = FORBIDDEN_BUSINESS_KEYS.some(pattern => lowerKey.includes(pattern));
    
    if (forbidden) {
      console.warn(
        `⚠️  WARNING: Storing potential business data in localStorage\n` +
        `Key: ${key}\n` +
        `This will cause data loss in production. Use API instead.`
      );
    }
    return;
  }
  
  // Production: Hard error
  const lowerKey = key.toLowerCase();
  const forbidden = FORBIDDEN_BUSINESS_KEYS.some(pattern => lowerKey.includes(pattern));
  
  if (forbidden) {
    throw new Error(
      '\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '🚨 FORBIDDEN: Business Data in localStorage\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '\n' +
      `Attempted to store: ${key}\n` +
      '\n' +
      'localStorage is NOT a database. Business data MUST go through API.\n' +
      '\n' +
      'Why this is forbidden:\n' +
      '  - Lost on browser clear\n' +
      '  - Not synced across devices\n' +
      '  - Cannot be queried by backend\n' +
      '  - Lost in private browsing\n' +
      '\n' +
      'Use API endpoints instead. Database is single source of truth.\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'
    );
  }
}

export function loadAll() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); }
  catch { return {}; }
}

export function saveSection(section, data) {
  // Guard against business data
  guardAgainstBusinessData(section);
  guardAgainstBusinessData(JSON.stringify(data).substring(0, 200)); // Check data content
  
  const all = loadAll();
  all[section] = { ...(all[section] || {}), ...data, _updatedAt: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(all));
  return all[section];
}

export function getSection(section) {
  const all = loadAll();
  return all[section] || null;
}
